import React, { useState, useEffect, useRef } from "react";
import { makeStyles } from "@material-ui/core/styles";
import { green } from "@material-ui/core/colors";
import Button from "@material-ui/core/Button";
import { Grid } from "@material-ui/core";
import Dialog from "@material-ui/core/Dialog";
import DialogContent from "@material-ui/core/DialogContent";
import DialogTitle from "@material-ui/core/DialogTitle";
import TextField from "@material-ui/core/TextField";
import { Autocomplete } from "@mui/material";
import { i18n } from "../../translate/i18n";

const useStyles = makeStyles((theme) => ({
    root: {
        display: "flex",
        flexWrap: "wrap",
    },
    btnWrapper: {
        position: "relative",
    },
}));

const FlowBuilderAddJumpModal = ({
    open,
    onSave,
    onUpdate,
    data,
    close,
    nodes // Receive all nodes to list as destinations
}) => {
    const classes = useStyles();
    const isMounted = useRef(true);

    const [activeModal, setActiveModal] = useState(false);
    const [selectedNode, setSelectedNode] = useState(null);
    const [nodeOptions, setNodeOptions] = useState([]);

    const [name, setName] = useState("");
    const [maxAttempts, setMaxAttempts] = useState(3);
    const [fallbackMessage, setFallbackMessage] = useState("");

    useEffect(() => {
        let activeOptions = [];
        if (nodes) {
            // Filter out the current node if we are editing (to avoid self-jump)
            // Also strictly filter to only jumpable nodes if needed, but for now any node is fine
            const nodeTypeLabels = {
                message: "Mensaje",
                input: "Entrada de Datos",
                conditionCompare: "Condición",
                flow: "Flujo",
                interval: "Intervalo",
                video: "Video",
                image: "Imagen",
                audio: "Audio",
                document: "Documento",
                question: "Pregunta",
                start: "Inicio",
                tag: "Etiqueta",
                kanban: "Kanban",
                typebot: "Typebot",
                webhook: "Webhook",
                httpRequest: "Petición HTTP",
                randomizer: "Aleatorizador",
                variable: "Variable",
                singleBlock: "Bloque Único"
            };

            activeOptions = nodes
                .filter(n => n.id !== data?.id && n.type !== 'start' && n.type !== 'interval' && n.type !== 'jump')
                .map(n => {
                    let displayName = (n.data && n.data.label) ? n.data.label : n.type;
                    let displayLabel = `${displayName} (${n.id})`;

                    if (n.data && n.data.name && n.data.name.trim() !== "") {
                        displayName = n.data.name;
                        displayLabel = `📌 ${n.data.name} (${n.type})`;
                    }

                    const typeLabel = nodeTypeLabels[n.type] || (n.type ? n.type.charAt(0).toUpperCase() + n.type.slice(1) : 'Desconocido');

                    return {
                        label: displayLabel,
                        id: n.id,
                        name: displayName,
                        type: n.type,
                        group: typeLabel
                    };
                });

            // Ordenar: Primero por Grupo (Tipo), luego por Nombre
            activeOptions.sort((a, b) => {
                if (a.group < b.group) return -1;
                if (a.group > b.group) return 1;
                if (a.label < b.label) return -1;
                if (a.label > b.label) return 1;
                return 0;
            });

            setNodeOptions(activeOptions);
        }

        if (open === "edit") {
            setActiveModal(true);
            setName(data.data.name || "");
            setMaxAttempts(data.data.maxAttempts || 3);
            setFallbackMessage(data.data.fallbackMessage || "");
            if (data.data && data.data.destinationNodeId) {
                const found = activeOptions.find(opt => opt.id === data.data.destinationNodeId);
                if (found) {
                    setSelectedNode(found);
                }
            }
        } else if (open === "create") {
            setActiveModal(true);
            setSelectedNode(null);
            setName("");
            setMaxAttempts(3);
            setFallbackMessage("");
        }

        return () => {
            isMounted.current = false;
        };
    }, [open, nodes, data]);

    const handleClose = () => {
        close(null);
        setActiveModal(false);
    };

    const handleSave = () => {
        if (!selectedNode) return;

        const payload = {
            destinationNodeId: selectedNode.id,
            destinationNodeName: selectedNode.name,
            name: name,
            maxAttempts: maxAttempts,
            fallbackMessage: fallbackMessage
        };

        // [ANTIGRAVITY] Fix: Propagate drop position if available
        if (open === "create" && data && data.position) {
            payload.position = data.position;
        }

        if (open === "edit") {
            onUpdate({
                ...data,
                data: { ...data.data, ...payload },
            });
        } else {
            onSave(payload);
        }
        handleClose();
        setName("");
    };

    return (
        <div className={classes.root}>
            <Dialog
                open={activeModal}
                onClose={handleClose}
                fullWidth
                maxWidth="sm"
                scroll="paper"
            >
                <DialogTitle id="form-dialog-title">
                    {open === "create" ? i18n.t("flowBuilder.jump.addTitle") : i18n.t("flowBuilder.jump.editTitle")}
                </DialogTitle>
                <DialogContent dividers>
                    <Grid container spacing={2}>
                        <Grid item xs={12}>
                            <TextField
                                label={i18n.t("flowBuilder.form.name") || "Nome"}
                                value={name}
                                onChange={(e) => setName(e.target.value)}
                                fullWidth
                                variant="outlined"
                                margin="dense"
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <Autocomplete
                                options={nodeOptions}
                                groupBy={(option) => option.group}
                                getOptionLabel={(option) => option.label || ""}
                                value={selectedNode}
                                onChange={(event, newValue) => {
                                    setSelectedNode(newValue);
                                }}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        label={i18n.t("flowBuilder.jump.selectDestination")}
                                        variant="outlined"
                                        fullWidth
                                        helperText={i18n.t("flowBuilder.jump.helperText")}
                                    />
                                )}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                type="number"
                                label={i18n.t("flowBuilder.jumpMaxAttempts") || "Límite de Intentos"}
                                value={maxAttempts}
                                onChange={(e) => {
                                    let val = parseInt(e.target.value);
                                    if (isNaN(val)) val = 1;
                                    if (val < 1) val = 1;
                                    if (val > 5) val = 5;
                                    setMaxAttempts(val);
                                }}
                                inputProps={{ min: 1, max: 5 }}
                                fullWidth
                                variant="outlined"
                                margin="dense"
                                helperText={i18n.t("flowBuilder.jumpMaxAttemptsHelper") || "Máximo de saltos seguidos (1-5)"}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                label={i18n.t("flowBuilder.jumpFallbackMessage") || "Mensaje de Límite"}
                                value={fallbackMessage}
                                onChange={(e) => setFallbackMessage(e.target.value)}
                                fullWidth
                                multiline
                                minRows={3}
                                variant="outlined"
                                margin="dense"
                                helperText={i18n.t("flowBuilder.jumpFallbackMessageHelper") || "Se enviará si se supera el límite."}
                            />
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogContent>
                    <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', paddingBottom: '10px' }}>
                        <Button onClick={handleClose} variant="outlined" style={{ borderColor: "#ff4d4f", color: "#ff4d4f", textTransform: "none", fontWeight: "bold", borderRadius: "8px" }}>
                            {i18n.t("common.cancel")}
                        </Button>
                        <Button
                            onClick={handleSave}
                            disabled={!selectedNode}
                            variant="contained" 
                            style={{ backgroundColor: "#7367F0", color: "#fff", textTransform: "none", fontWeight: "bold", borderRadius: "8px", boxShadow: "0 4px 14px 0 rgba(115, 103, 240, 0.39)" }}
                        >
                            {open === "create" ? i18n.t("common.add") : i18n.t("common.save")}
                        </Button>
                    </div>
                </DialogContent>
            </Dialog>
        </div>
    );
};

export default FlowBuilderAddJumpModal;
